import React, { useState } from 'react';
import { useAppConfigContext } from '@/context/AppConfig';
import { ReportSection } from '@/reports/index';
import { serializedTypes } from '@/reports/types';
import { Text, Icons, Card, Grid, Table, styled } from '@/theme/index';
import {
  TableContainer,
  StyledAccordion,
  TableTitle,
  AdjustedAccordionSummary,
  AdjustedAccordionDetails,
  CurrentLocationContainer,
} from './components';

export interface BuildingPermitsProps {
  propertyAddress: string;
  property: serializedTypes.SerializedProperty;
  permalink?: string;
}

const StyledTablePrimaryData = styled(Table)(({ theme }) => ({
  td: {
    fontSize: '16px',
    padding: '2px 0',
  },
  'td:first-of-type': {
    fontWeight: 'bold',
    width: '126px',
    padding: '2px 0',
    [theme.breakpoints.up('sm')]: {
      width: '145px',
    },
  },
}));

export const StyledText = styled('p')(({ theme }) => ({
  color: theme.palette.secondary.contrastText,
}));

export function BuildingPermits({
  propertyAddress,
  property,
  permalink,
}: BuildingPermitsProps) {
  const { trackEvent } = useAppConfigContext();
  const hasBuildingPermits = Boolean(
    property?.buildingPermitsOfProperty?.length
  );
  return (
    <ReportSection
      title="Building Permits"
      sectionId="building-permits-section"
      hasContent={hasBuildingPermits}
      propertyAddress={propertyAddress}
      permalink={permalink}
    >
      {hasBuildingPermits && (
        <StyledText>
          Authorization from a legal or regulatory body for the construction of
          a new building.
        </StyledText>
      )}
      {property?.buildingPermitsOfProperty?.map((buildingPermit, index) => {
        const [expanded, setExpanded] = useState(index === 0);
        const handleOpen = (event: { stopPropagation: () => void }) => {
          trackEvent('expand', 'engagement click', 'building permits section');
          event.stopPropagation();
          setExpanded(true);
        };
        const handleClose = (event: { stopPropagation: () => void }) => {
          trackEvent(
            'collapse',
            'engagement click',
            'building permits section'
          );
          event.stopPropagation();
          setExpanded(false);
        };
        return (
          <Card variant="outlined" sx={{ mb: 2.5, padding: 2.5 }} key={index}>
            <TableContainer>
              <StyledAccordion expanded={expanded}>
                <AdjustedAccordionSummary
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <Grid container>
                    <Grid item container alignItems="center" xs={8}>
                      <Text variant="h2">
                        {' '}
                        Addition of rooms or other spaces
                      </Text>
                    </Grid>
                    <Grid item sx={{ marginLeft: 'auto' }}>
                      {expanded ? (
                        <Icons.ExpandCircleDown
                          color="primary"
                          onClick={handleClose}
                          sx={{ transform: 'rotate(180deg)' }}
                          fontSize="small"
                        />
                      ) : (
                        <Icons.ExpandCircleDown
                          color="primary"
                          onClick={handleOpen}
                          fontSize="small"
                        />
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <StyledTablePrimaryData
                        rows={buildingPermit.permitsData.slice(0, 3)}
                      />
                      <CurrentLocationContainer>
                        <TableTitle>Current Location</TableTitle>
                        <Text>{propertyAddress}</Text>
                      </CurrentLocationContainer>
                    </Grid>
                  </Grid>
                </AdjustedAccordionSummary>
                <AdjustedAccordionDetails>
                  <Table
                    className="striped flipped"
                    rows={buildingPermit.permitsData.slice(
                      3,
                      buildingPermit.permitsData.length
                    )}
                  />
                </AdjustedAccordionDetails>
              </StyledAccordion>
            </TableContainer>
          </Card>
        );
      })}
    </ReportSection>
  );
}
